"use client";

import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { dismissDemoBannerClient, DEMO_BANNER_COOKIE } from "@/lib/demo-banner";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";

const GOLD = "font-semibold text-gold";

/** Muted divider between languages. Padding (not spaces) gives the gap so it
 *  can't collapse under `white-space: nowrap`; aria-hidden since it's decorative
 *  punctuation, not content. */
function Sep() {
  return (
    <span aria-hidden="true" className="px-[0.7em] text-white/30">
      {"//"}
    </span>
  );
}

/** Bilingual message; the "pre-launch" clause is highlighted gold, each sentence
 *  ends with a period. Shared by the ticker copies and the reduced-motion static
 *  fallback so they can't drift. No launch date — the banner just signals
 *  pre-launch status until it's removed at launch. */
function Message() {
  return (
    <>
      This is a demo of Easytiger.vn, the platform is <span className={GOLD}>still at pre-launch stage</span>.
      <Sep />
      Đây là phiên bản demo của Easytiger.vn, nền tảng vẫn đang <span className={GOLD}>trong giai đoạn trước khi ra mắt</span>.
    </>
  );
}

/**
 * Temporary pre-launch ribbon with a bilingual scrolling ticker. Rendered as
 * the first child INSIDE the fixed Nav wrapper (Nav.tsx → NavScrollWrapper) so
 * its height folds into the spacer / `--sticky-top` math automatically.
 *
 * Self-gating client-side so the Nav can render it unconditionally and the
 * page stays statically cacheable (cacheComponents): it renders in the cached
 * HTML, then hides on mount only if the visitor has dismissed it (cookie).
 * Dismissers see a brief flash before it self-hides — accepted trade for a
 * cacheable shell. There is no launch-date auto-hide anymore; delete this whole
 * banner (this file + demo-banner.ts + demo-banner-server.ts + the <DemoBanner />
 * in Nav.tsx) at launch.
 */
export default function DemoBanner() {
  const [hidden, setHidden] = useState(false);
  const reducedMotion = useReducedMotion();

  // Hide only if previously dismissed. Runs after the cached HTML paints, so the
  // common (not-dismissed) case shows immediately.
  useEffect(() => {
    const dismissed = document.cookie.split("; ").some((c) => c === `${DEMO_BANNER_COOKIE}=1`);
    if (dismissed) setHidden(true);
  }, []);

  if (hidden) return null;

  return (
    <div
      role="region"
      // Bilingual like the message itself: this banner is deliberately
      // locale-INDEPENDENT (it shows EN and VI at once), so translating only
      // its aria-labels would add a locale dependency the component exists
      // without. Screen readers announce both, exactly as sighted users see both.
      aria-label="Site notice · Thông báo"
      className="relative flex items-center bg-navy text-white"
    >
      {reducedMotion ? (
        // Reduced-motion: no scroll — full text in the DOM (read in full by AT),
        // clipped to one line for sighted users.
        <p className="min-w-0 flex-1 truncate py-8 pl-21 pr-8 text-xs sm:text-sm">
          <Message />
        </p>
      ) : (
        // Seamless ticker: two identical copies; `.ticker-track` slides left by
        // exactly one copy-width then snaps back. The trailing <Sep /> divides
        // the loop seam; the duplicate is aria-hidden so AT sees the message once.
        <div className="min-w-0 flex-1 overflow-hidden py-8" aria-roledescription="marquee">
          <div
            className="ticker-track flex"
            style={{ width: "max-content", animationDuration: "32s" }}
          >
            <span className="whitespace-nowrap text-xs sm:text-sm">
              <Message />
              <Sep />
            </span>
            <span aria-hidden="true" className="whitespace-nowrap text-xs sm:text-sm">
              <Message />
              <Sep />
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        aria-label="Dismiss notice · Đóng thông báo"
        onClick={() => {
          dismissDemoBannerClient();
          setHidden(true);
        }}
        className="mr-8 flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-control text-white/70 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:mr-13"
      >
        <X size={15} weight="bold" aria-hidden="true" />
      </button>
    </div>
  );
}
